import { ExternalLinkIcon } from "lucide-react";

import { LiquidityDroplet } from "./LiquidityDroplet";

import { Column, Row, Text } from "@/components";
import { LaborupGear } from "@/components/LaborupGear";

import { laborupGearColor } from "@/lib/constants";
import { JobLiquidity, JobLiquidityBreakdown, PositionCategory } from "@/types";

// Source of the workforce denominators (OEWS explorer), linked from the card.
const BLS_OEWS_URL = "https://data.bls.gov/oes/#/home";

// Plural worker nouns for the "N <workers> in this market" line, keyed by the
// scored category the BLS denominator counts.
const categoryWorkerNouns: Record<PositionCategory, string> = {
  [PositionCategory.PRODUCTION]: "production workers",
  [PositionCategory.MACHINING]: "machinists",
  [PositionCategory.WELDING]: "welders",
  [PositionCategory.MAINTENANCE]: "maintenance technicians",
  [PositionCategory.ENGINEERING]: "engineers",
};

// Mirrors the Liquidity Score RFC's per-tier starting actions.
const LIQUIDITY_GUIDANCE: Record<
  JobLiquidity,
  { guidance: string; title: string }
> = {
  [JobLiquidity.UNKNOWN]: {
    guidance:
      "Insufficient market data. Run internal and external in parallel.",
    title: "Unknown",
  },
  [JobLiquidity.THIN]: {
    guidance: "Launch external immediately.",
    title: "Low",
  },
  [JobLiquidity.MODERATE]: {
    guidance: "Run internal and external in parallel.",
    title: "Moderate",
  },
  [JobLiquidity.LIQUID]: {
    guidance: "Start internal. Add external if the pipeline stalls.",
    title: "High",
  },
  [JobLiquidity.VERY_LIQUID]: {
    guidance: "Start with Search and Sally. No external at launch.",
    title: "Very High",
  },
};

// Hover card behind the liquidity droplet: tier + guidance up top, then the
// market's evidence — what we know about the pool, in staffer terms. The
// formula's numeric scores and weights stay internal; the raw facts are what
// a user can act on.
export const LiquidityBreakdownCard = ({
  breakdown,
  level,
}: {
  breakdown?: JobLiquidityBreakdown | null;
  level: JobLiquidity;
}) => {
  /* Helpers */
  const { guidance, title } = LIQUIDITY_GUIDANCE[level];

  /* Render */
  const renderEvidenceTile = (
    label: string,
    value: string,
    caption: string,
  ) => (
    <Column className="gap-0.5 rounded-lg bg-lever-surface-default-on-elevation px-3 py-2.5">
      <Text className="text-[10px] font-medium uppercase tracking-wide text-lever-text-label">
        {label}
      </Text>
      <Text className="text-lg font-bold tabular-nums leading-tight text-lever-text-body">
        {value}
      </Text>
      <Text className="text-xs font-normal leading-snug text-lever-text-label">
        {caption}
      </Text>
    </Column>
  );

  const renderMarketName = () => (
    <Column className="gap-0.5">
      <Text className="text-sm font-medium text-lever-text-body">
        {breakdown?.marketName}
      </Text>
      {breakdown?.workforceCount ? (
        <a
          className="group w-fit"
          href={BLS_OEWS_URL}
          // The card portals over table rows with their own click handlers
          // (e.g. portfolio row -> job navigation); the click must stay here.
          onClick={(event) => {
            event.stopPropagation();
          }}
          rel="noreferrer"
          target="_blank"
        >
          <Text className="whitespace-nowrap text-xs font-normal text-lever-text-label group-hover:text-lever-text-body group-hover:underline">
            {breakdown.workforceCount.toLocaleString()}{" "}
            {/* "workers" fallback: a backend without positionCategory yet
                sends breakdowns missing the field */}
            {categoryWorkerNouns[breakdown.positionCategory] || "workers"} (BLS
            data) <ExternalLinkIcon className="-mt-0.5 inline h-3 w-3" />
          </Text>
        </a>
      ) : null}
    </Column>
  );

  const renderBreakdown = () => {
    if (!breakdown) return null;

    if (breakdown.score === null) {
      return (
        <Column className="gap-2 border-t border-lever-border-light p-4">
          {renderMarketName()}
          <Text className="rounded-lg bg-lever-surface-default-on-elevation px-3 py-2.5 text-xs font-normal leading-snug text-lever-text-label">
            No workforce data for this market yet, so the score cannot be
            computed ({breakdown.strongCount.toLocaleString()} strong candidates
            known).
          </Text>
        </Column>
      );
    }

    return (
      <Column className="gap-2 border-t border-lever-border-light p-4">
        {renderMarketName()}
        <Row className="items-center gap-1 pt-1">
          <LaborupGear color={laborupGearColor} height={12} width={12} />
          <Text className="text-[10px] font-medium uppercase tracking-wide text-lever-text-label">
            On Laborup
          </Text>
        </Row>
        <div className="grid grid-cols-2 gap-2">
          {renderEvidenceTile(
            "Supply",
            breakdown.strongCount.toLocaleString(),
            "strong candidates",
          )}
          {renderEvidenceTile(
            "Freshness",
            breakdown.active90StrongCount.toLocaleString(),
            "active in 90d",
          )}
          {renderEvidenceTile(
            "Proof",
            breakdown.placedTrailing12mo.toLocaleString(),
            "placements in 12mo",
          )}
          {breakdown.medianTimeToFirstFillDays === null
            ? renderEvidenceTile("Velocity", "—", "no fills yet")
            : renderEvidenceTile(
                "Velocity",
                `${breakdown.medianTimeToFirstFillDays.toFixed(1)}d`,
                "median first fill",
              )}
        </div>
        {breakdown.underTested ? (
          <Text className="rounded-lg bg-lever-surface-default-on-elevation px-3 py-2.5 text-xs font-normal italic leading-snug text-lever-text-label">
            Under-tested. Light placement history. Supply is proven, demand
            history is not yet.
          </Text>
        ) : null}
      </Column>
    );
  };

  const render = () => (
    <Column className="w-80">
      <Column className="gap-2.5 p-4">
        <Row className="items-center gap-2">
          <LiquidityDroplet className="h-4 w-4" level={level} />
          <Text className="text-sm font-semibold text-lever-text-body">
            {title}
          </Text>
        </Row>
        <Column className="gap-1 rounded-md bg-lever-surface-default-on-elevation px-3 py-2.5">
          <Text className="text-[10px] font-medium uppercase tracking-wide text-lever-text-label">
            Strategy
          </Text>
          <Text className="text-sm font-normal leading-snug text-lever-text-body">
            {guidance}
          </Text>
        </Column>
      </Column>
      {renderBreakdown()}
    </Column>
  );

  return render();
};
